import { useEffect, useMemo, useState } from "react";
import { SelectPicker } from "rsuite";
import { useParams } from "react-router-dom";

import { Button } from "reactstrap";
import { Parser } from "hot-formula-parser";

import useModule from "../../../../hooks/useModule";
import FieldUtils from "../../../../utils/FieldUtils";
import DisplayName from "../../../Inputs/DisplayName";

interface Props {
  fieldValues: any;
  setFieldValues: (newValues: any) => void;
  setDisabled?: (val: boolean) => void;
  defaultType?: CalculatedTypes;
  defaultOutput?: CalculatedOutputs;
  disabledSelection?: boolean;
  fieldToIgnore?: string;
  styles?: string;
}

type CalculatedTypes = "string" | "number";
export type CalculatedOutputs = "number" | "currency" | "percentage" | "";
interface Calculated {
  Type: string;
  Formula: string;
  FieldAPINames: string[];
  PlainText: string;
  Output?: CalculatedOutputs;
}

const getDefaultValues = () => {
  return {
    Type: "",
    Formula: "",
    FieldAPINames: [],
    PlainText: "",
  };
};

const valueOptions = [
  {
    id: 1,
    label: "Text Calculated Field",
    value: "string",
  },
  {
    id: 2,
    label: "Number Calculated Field",
    value: "number",
  },
];

const outputFormatOptions = [
  {
    id: 1,
    label: "Number",
    value: "number",
  },
  {
    id: 2,
    label: "Currency",
    value: "currency",
  },
  {
    id: 3,
    label: "Percentage",
    value: "percentage",
  },
];

const FieldTypeCalculated = ({
  fieldValues,
  setFieldValues,
  setDisabled,
  defaultType,
  defaultOutput,
  disabledSelection = false,
  fieldToIgnore,
  styles,
}: Props) => {
  const params = useParams<{ APIName: string }>();
  const { module } = useModule(params?.APIName);

  const existingValues = fieldValues?.calculatedFieldData;
  const [type, setType] = useState<CalculatedTypes>(
    defaultType ? defaultType : existingValues?.Type ?? ""
  );
  const [output, setOutput] = useState<CalculatedOutputs>(
    defaultOutput ? defaultOutput : existingValues?.Output ?? ""
  );
  const [calculatedValues, setCalculatedValues] = useState<Calculated>(
    existingValues ?? getDefaultValues()
  ); // The local calculated values state. Gets passed along to fieldValues when saved.

  const [isValidFormula, setIsValidFormula] = useState(!!existingValues);
  const [isInvalidOutput, setIsInvalidOutput] = useState(
    calculatedValues.Type === "number" && !calculatedValues?.Output
  );
  const [isValidated, setIsValidated] = useState(!!existingValues?.Formula);

  /**
   * 1 - Formula must exist to be valid
   * 2 - Handle number type validation
   */
  const validFormula = useMemo(() => {
    if (!calculatedValues || !calculatedValues.Formula) return false;

    let formulaWithoutVariables = calculatedValues.Formula.replace(
      /\s/g,
      ""
    ).replace(/\{\{.*?\}\}/g, "1");

    formulaWithoutVariables = addLeadingZeroToDecimals(formulaWithoutVariables);

    // TODO: Implement shunting yard algorithm to validate it (Gustavo!!)
    const parser = new Parser();
    const result = parser.parse(formulaWithoutVariables);

    return !result.error;
  }, [calculatedValues.Formula]);

  // Calculate the valid fields that can be included.
  const validFields = FieldUtils.validCalculatedField(
    module,
    type,
    fieldToIgnore
  );

  const handleChangeType = (type: CalculatedTypes) => {
    setType(type);
    setOutput("");
    setCalculatedValues({
      Type: type,
      Formula: "",
      FieldAPINames: [],
      PlainText: "",
    });
    setFieldValues((fieldValues: any) => ({
      ...fieldValues,
      calculatedFieldData: "",
      defaultValue: null, // Added this to reset when using it for Currency, CurrencyInterval, Number, Percentage fields.
    }));

    setDisabled && setDisabled(true);
  };
  const handleChangeOutput = (output: CalculatedOutputs) => {
    setOutput(output);
    setCalculatedValues((prev) => {
      return { ...prev, Output: output };
    });

    setFieldValues((fieldValues: any) => ({
      ...fieldValues,
      calculatedFieldData: "",
    }));

    setDisabled && setDisabled(true);
  };

  const handleChange = (val: {
    newValue: string;
    mentionFieldAPINames: string[];
    newPlainTextValue: string;
  }) => {
    setIsValidated(false);
    setCalculatedValues({
      Type: type,
      Formula: addLeadingZeroToDecimals(val.newValue),
      FieldAPINames: val.mentionFieldAPINames,
      PlainText: addLeadingZeroToDecimals(val.newPlainTextValue),
      ...(output && { Output: output }),
    });

    if (fieldValues.calculatedFieldData) {
      setFieldValues((fieldValues: any) => ({
        ...fieldValues,
        calculatedFieldData: "",
        defaultValue: null, // Added this to reset when using it for Currency, CurrencyInterval, Number, Percentage fields.
      }));
      setDisabled && setDisabled(true);
    }
  };

  const handleClearFormula = () => {
    setCalculatedValues({
      Type: type,
      Formula: "",
      FieldAPINames: [],
      PlainText: "",
      Output: output,
    });
    setFieldValues((fieldValues: any) => ({
      ...fieldValues,
      calculatedFieldData: "",
      defaultValue: null, // Added this to reset when using it for Currency, CurrencyInterval, Number, Percentage fields.
    }));
    setDisabled?.(true);
  };

  const handleSaveFormula = () => {
    setIsValidated(true);

    if (calculatedValues.Type === "number" && !validFormula) {
      return setIsValidFormula(false);
    }
    if (calculatedValues.Type === "string" && !calculatedValues.Formula) {
      return setIsValidFormula(false);
    }

    if (calculatedValues.Type === "number" && !calculatedValues?.Output) {
      setIsInvalidOutput(true);
      return;
    }

    setIsValidFormula(true);
    setIsInvalidOutput(false);
    setFieldValues((fieldValues: any) => ({
      ...fieldValues,
      calculatedFieldData: calculatedValues,
      defaultValue: null,
    }));

    setDisabled && setDisabled(false);
  };

  useEffect(() => {
    if (!fieldValues?.calculatedFieldData?.Formula) {
      setDisabled && setDisabled(true);
    }
  }, []);

  return (
    <div
      style={{
        background: "var(--body-background, #FFF)",
        boxShadow: "0px 1px 8px 2px rgba(0, 0, 0, 0.15)",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        alignContent: "flex-start",
        flexWrap: "wrap",
        padding: 20,
      }}
      className={styles ? styles : "mt-5"}
    >
      <div className="row mb-4 w-100">
        <div className="col-6">
          <h4>Return Data Type</h4>
          <SelectPicker
            data={valueOptions}
            style={{ width: "100%" }}
            onChange={(val: any) => handleChangeType(val)}
            value={type}
            searchable={false}
            disabled={disabledSelection}
          />
        </div>

        {type && type === "number" && !disabledSelection && (
          <div className="col-6">
            <h4>Output Format</h4>
            <SelectPicker
              data={outputFormatOptions}
              style={{ width: "100%" }}
              onChange={(val: any) => handleChangeOutput(val)}
              value={output}
              searchable={false}
            />
          </div>
        )}
      </div>

      <h4>Formula Expression:</h4>
      <DisplayName
        fields={validFields}
        existingValue={
          calculatedValues?.Formula ? calculatedValues.Formula : ""
        }
        onChange={(val: {
          newValue: string;
          mentionFieldAPINames: string[];
          newPlainTextValue: string;
        }) => handleChange(val)}
        readOnly={type ? false : true}
        size={"sm"}
        isSelfReference={false}
      />
      {calculatedValues.Formula && isValidated && (
        <div className="mt-2">
          {isInvalidOutput ? (
            <h5 className="font-weight-normal" style={{ color: "red" }}>
              Invalid output selected
            </h5>
          ) : isValidFormula ? (
            <h5
              className="font-weight-normal"
              style={{ color: "var(--primary)" }}
            >{`${fieldValues.name} = ${calculatedValues.PlainText}`}</h5>
          ) : (
            <h5 className="font-weight-normal" style={{ color: "red" }}>
              Invalid formula
            </h5>
          )}
        </div>
      )}
      <div className="d-flex mt-3" style={{ marginLeft: "auto" }}>
        <Button size="sm" outline onClick={handleClearFormula}>
          Clear
        </Button>
        <Button
          size="sm"
          style={{
            border: "1px solid var(--grey-200-bdc-9-c-2, #BDC9C2)",
            background: "var(--hover-nav-items, #F2F6F4)",
            color: "#031108",
          }}
          onClick={handleSaveFormula}
        >
          Save Formula
        </Button>
      </div>
    </div>
  );
};

function addLeadingZeroToDecimals(formula: string) {
  if (!formula) return formula;

  // Match decimal numbers without a number in front
  const regex = /(^|\D)(\.\d+)/g;

  // Replace the matching patterns with a leading zero
  const updatedFormula = formula.replace(regex, "$10$2");

  return updatedFormula;
}

export default FieldTypeCalculated;
